import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FileUploadComponent = ({ 
  onFileUpload, 
  uploadedFile,
  error,
  disabled = false 
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const maxFileSize = 5 * 1024 * 1024; // 5MB

  const validateFile = (file) => {
    if (!allowedTypes?.includes(file?.type)) {
      return 'Solo se permiten archivos JPG, PNG o WebP';
    }
    if (file?.size > maxFileSize) {
      return 'El archivo no debe superar los 5MB';
    }
    return null;
  };

  const processFile = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      onFileUpload(null, validationError);
      return;
    }

    setIsUploading(true);

    try {
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      // Simulate file compression/processing
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create file object with metadata
      const fileData = {
        file: file,
        name: file?.name,
        size: file?.size,
        type: file?.type,
        lastModified: file?.lastModified,
        preview: url
      };

      onFileUpload(fileData, null);
    } catch (err) {
      onFileUpload(null, 'Error al procesar el archivo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e?.dataTransfer?.files);
    if (files?.length > 0) {
      processFile(files?.[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    if (files?.length > 0) {
      processFile(files?.[0]);
    }
  };

  const handleRemoveFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    onFileUpload(null, null);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-luxury">
      <div className="flex items-center space-x-2 mb-6">
        <div className="p-2 bg-secondary/10 rounded-lg">
          <Icon name="Upload" size={20} className="text-secondary" />
        </div>
        <div>
          <h3 className="text-lg font-heading font-semibold text-gray-800">
            Comprobante de Pago
          </h3>
          <p className="text-sm text-gray-800 font-caption">
            Sube una foto clara de tu comprobante
          </p>
        </div>
      </div>
      {!uploadedFile ? (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-premium ${
            isDragOver && !disabled
              ? 'border-secondary bg-secondary/5'
              : error
              ? 'border-error bg-error/5' :'border-border hover:border-secondary/50 hover:bg-muted/30'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={() => !disabled && fileInputRef?.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled}
          />

          {isUploading ? (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mx-auto">
                <Icon name="RefreshCw" size={24} className="text-secondary animate-spin" />
              </div>
              <div>
                <h4 className="font-body font-medium text-gray-800">
                  Procesando archivo...
                </h4>
                <p className="text-sm text-gray-800 font-caption">
                  Comprimiendo y validando imagen
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${
                error ? 'bg-error/20' : 'bg-secondary/20'
              }`}>
                <Icon 
                  name={error ? "AlertCircle" : isDragOver ? "Download" : "Upload"} 
                  size={24} 
                  className={error ? 'text-error' : 'text-secondary'} 
                />
              </div>
              
              <div>
                <h4 className={`font-body font-medium ${error ? 'text-error' : 'text-gray-800'}`}>
                  {error ? 'Error al subir archivo': isDragOver ?'Suelta el archivo aquí' : 'Arrastra tu comprobante aquí'}
                </h4>
                <p className="text-sm text-gray-800 font-caption mt-1">
                  {error || 'o haz clic para seleccionar desde tu dispositivo'}
                </p>
              </div>

              {/* Botón "Seleccionar Archivo" — Arreglado aquí con efecto verde */}
              {!error && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={disabled}
                  iconName="FolderOpen"
                  iconPosition="left"
                  className="mt-2 hover:bg-green-500 active:bg-green-600 focus:ring-2 focus:ring-green-300 transition-colors" // Cambiado aquí
                >
                  Seleccionar Archivo
                </Button>
              )}
            </div>
          )}

          <div className="mt-4 text-xs text-gray-800 font-caption">
            Formatos: JPG, PNG, WebP • Máximo: 5MB
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* File Preview */}
          <div className="bg-muted/30 rounded-lg p-4 border border-golden/20">
            <div className="flex items-start space-x-4">
              {previewUrl && (
                <div className="w-20 h-20 rounded-lg overflow-hidden border border-border">
                  <Image
                    src={previewUrl}
                    alt="Vista previa del comprobante de pago subido"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <h4 className="font-body font-medium text-gray-800 text-sm">
                  {uploadedFile?.name}
                </h4>
                <p className="text-xs text-gray-800 font-caption mt-1">
                  {formatFileSize(uploadedFile?.size)} • {uploadedFile?.type}
                </p>
                
                <div className="flex items-center space-x-1 mt-2">
                  <Icon name="CheckCircle" size={12} className="text-success" />
                  <span className="text-xs text-success font-caption">
                    Archivo cargado correctamente
                  </span>
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleRemoveFile}
                disabled={disabled}
                className="h-8 w-8"
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
          </div>

          {/* Upload Another File */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef?.current?.click()}
            disabled={disabled}
            iconName="RefreshCw"
            iconPosition="left"
            className="w-full"
          >
            Cambiar Archivo
          </Button>
        </div>
      )}
      {error && (
        <div className="mt-4 p-3 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-error" />
            <span className="text-sm text-error font-caption">{error}</span>
          </div>
        </div>
      )}
      <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Camera" size={16} className="text-accent mt-0.5" />
          <div>
            <h4 className="font-body font-medium text-gray-800 text-sm">
              Consejos para una mejor foto
            </h4>
            <ul className="text-xs text-gray-800 font-caption mt-1 space-y-1">
              <li>• Asegúrate de que todos los datos sean legibles</li>
              <li>• Usa buena iluminación, evita sombras</li>
              <li>• Incluye la referencia y el monto completo</li>
              <li>• La imagen debe estar enfocada y sin borrosidad</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadComponent;